import React, { useState } from 'react';
import { Key, AlertCircle, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Alert, AlertDescription } from './ui/alert';
import { getApiUrl } from '../config';

const LicensePromptModal = ({ user, onActivationSuccess }) => {
  const [licenseKey, setLicenseKey] = useState('');
  const [activating, setActivating] = useState(false);
  const [error, setError] = useState(null);
  const [showTransferOption, setShowTransferOption] = useState(false);

  const getDeviceInfo = () => {
    return {
      name: navigator.platform || 'Unknown Device',
      os: navigator.userAgent.includes('Win') ? 'Windows' :
          navigator.userAgent.includes('Mac') ? 'macOS' :
          navigator.userAgent.includes('Linux') ? 'Linux' : 'Unknown',
      browser: navigator.userAgent.includes('Chrome') ? 'Chrome' :
                navigator.userAgent.includes('Firefox') ? 'Firefox' :
                navigator.userAgent.includes('Safari') ? 'Safari' : 'Unknown',
      platform: navigator.platform,
      screen_resolution: `${window.screen.width}x${window.screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };
  };

  const handleActivate = async () => {
    if (!licenseKey.trim()) {
      setError('Please enter your license key');
      return;
    }

    setActivating(true);
    setError(null);

    try {
      const API_URL = getApiUrl();
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_URL}/api/license/activate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          license_key: licenseKey.trim().toUpperCase(),
          device_info: getDeviceInfo()
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'License activation failed');
      }

      // Success! Call parent callback
      if (onActivationSuccess) {
        onActivationSuccess(data);
      }

    } catch (err) {
      // Check if error is about device limit
      if (err.message.includes('License limit reached') || err.message.includes('device')) {
        setShowTransferOption(true);
      }
      setError(err.message);
    } finally {
      setActivating(false);
    }
  };

  const handleTransferLicense = async () => {
    if (!licenseKey.trim()) {
      setError('Please enter your license key');
      return;
    }

    if (!window.confirm('This will deactivate your license on all other devices and activate it on this device. Continue?')) {
      return;
    }

    setActivating(true);
    setError(null);

    try {
      const API_URL = getApiUrl();
      const token = localStorage.getItem('token');

      // First, deactivate all devices
      const deactivateResponse = await fetch(`${API_URL}/api/license/transfer`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          license_key: licenseKey.trim().toUpperCase(),
          device_info: getDeviceInfo()
        })
      });

      const data = await deactivateResponse.json();

      if (!deactivateResponse.ok) {
        throw new Error(data.detail || 'License transfer failed');
      }

      // Success! Call parent callback
      if (onActivationSuccess) {
        onActivationSuccess(data);
      }

    } catch (err) {
      setError(err.message);
    } finally {
      setActivating(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !activating) {
      handleActivate();
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.85)',
      backdropFilter: 'blur(8px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '20px'
    }}>
      <Card style={{
        background: 'rgba(15, 20, 30, 0.98)',
        backdropFilter: 'blur(20px)',
        border: '2px solid rgba(0, 212, 255, 0.3)',
        borderRadius: '1.5rem',
        maxWidth: '550px',
        width: '100%',
        boxShadow: '0 20px 60px rgba(0, 212, 255, 0.2)'
      }}>
        <CardHeader style={{ textAlign: 'center', paddingBottom: '10px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '15px'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #00d4ff 0%, #0099cc 100%)',
              borderRadius: '50%',
              padding: '20px',
              boxShadow: '0 8px 20px rgba(0, 212, 255, 0.4)'
            }}>
              <Key className="h-8 w-8" style={{ color: '#ffffff' }} />
            </div>
          </div>
          <CardTitle style={{ color: '#ffffff', fontSize: '24px', marginBottom: '8px' }}>
            Activate Your License
          </CardTitle>
          <CardDescription style={{ color: '#94a3b8', fontSize: '15px' }}>
            Enter the license key from your welcome email to unlock full access
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4" style={{ padding: '20px 30px 30px' }}>
          {error && (
            <Alert style={{
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '0.75rem'
            }}>
              <AlertCircle className="h-4 w-4" style={{ color: '#ef4444' }} />
              <AlertDescription style={{ color: '#ef4444' }}>
                {error}
              </AlertDescription>
            </Alert>
          )}

          {showTransferOption && (
            <Alert style={{
              background: 'rgba(251, 191, 36, 0.1)',
              border: '1px solid rgba(251, 191, 36, 0.3)',
              borderRadius: '0.75rem',
              marginTop: '12px'
            }}>
              <AlertCircle className="h-4 w-4" style={{ color: '#fbbf24' }} />
              <AlertDescription style={{ color: '#fbbf24' }}>
                <strong>Need to transfer your license?</strong>
                <br />
                Click "Transfer License" below to deactivate all other devices and activate on this one.
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <label style={{
              color: '#e2e8f0',
              fontSize: '14px',
              fontWeight: '500',
              display: 'block',
              marginBottom: '8px'
            }}>
              License Key
            </label>
            <Input
              type="text"
              placeholder="XXXX-XXXX-XXXX-XXXX-XXXX"
              value={licenseKey}
              onChange={(e) => setLicenseKey(e.target.value.toUpperCase())}
              onKeyPress={handleKeyPress}
              maxLength={24}
              autoFocus
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(0, 212, 255, 0.3)',
                color: '#ffffff',
                fontFamily: 'monospace',
                fontSize: '16px',
                letterSpacing: '2px',
                padding: '12px 16px',
                borderRadius: '0.75rem',
                transition: 'all 0.2s'
              }}
            />
            <p style={{ color: '#94a3b8', fontSize: '12px', marginTop: '6px' }}>
              Check your email for your license key (20 characters)
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ display: 'flex', gap: '10px' }}>
              <Button
                onClick={handleActivate}
                disabled={activating || !licenseKey.trim()}
                style={{
                  flex: 1,
                  background: 'linear-gradient(135deg, #00d4ff 0%, #0099cc 100%)',
                  color: '#ffffff',
                  fontWeight: '600',
                  padding: '14px',
                  fontSize: '16px',
                  borderRadius: '0.75rem',
                  border: 'none',
                  cursor: (activating || !licenseKey.trim()) ? 'not-allowed' : 'pointer',
                  opacity: (activating || !licenseKey.trim()) ? 0.6 : 1,
                  transition: 'all 0.2s',
                  boxShadow: '0 4px 12px rgba(0, 212, 255, 0.3)'
                }}
              >
                {activating ? 'Activating...' : 'Activate License'}
              </Button>
            </div>

            {showTransferOption && (
              <Button
                onClick={handleTransferLicense}
                disabled={activating || !licenseKey.trim()}
                style={{
                  width: '100%',
                  background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
                  color: '#ffffff',
                  fontWeight: '600',
                  padding: '14px',
                  fontSize: '16px',
                  borderRadius: '0.75rem',
                  border: 'none',
                  cursor: (activating || !licenseKey.trim()) ? 'not-allowed' : 'pointer',
                  opacity: (activating || !licenseKey.trim()) ? 0.6 : 1,
                  transition: 'all 0.2s',
                  boxShadow: '0 4px 12px rgba(251, 191, 36, 0.3)'
                }}
              >
                {activating ? 'Transferring...' : 'Transfer License to This Device'}
              </Button>
            )}
          </div>

          <div style={{
            marginTop: '20px',
            padding: '16px',
            borderRadius: '0.75rem',
            background: 'rgba(0, 212, 255, 0.05)',
            border: '1px solid rgba(0, 212, 255, 0.2)'
          }}>
            <p style={{
              color: '#94a3b8',
              fontSize: '13px',
              lineHeight: '1.6',
              margin: 0
            }}>
              <strong style={{ color: '#00d4ff' }}>Can't find your license key?</strong>
              <br />
              Check your email (including spam folder) for "Your CyberGuard License Key" or contact support.
            </p>
          </div>

          <div style={{
            marginTop: '12px',
            padding: '12px',
            borderRadius: '0.75rem',
            background: 'rgba(251, 191, 36, 0.05)',
            border: '1px solid rgba(251, 191, 36, 0.2)'
          }}>
            <p style={{
              color: '#fbbf24',
              fontSize: '12px',
              lineHeight: '1.5',
              margin: 0
            }}>
              <strong>🔒 Security Notice:</strong> Each license is bound to your devices to prevent unauthorized sharing.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LicensePromptModal;
